/**
 * Review queue page (B19) — per `feature-spec.md` §3.5.1.
 *
 * Mounted at `/v4/go-lab/annotate/review` under `AnnotateLayout`, which
 * owns the page chrome (H1 "Annotate" + tab nav). Org-wide table of
 * records awaiting a manager decision: `disputed` rows need adjudication,
 * `agreed` rows need one-click sign-off. Both surface in one queue so
 * managers have a single inbox.
 *
 * Row click branches by status:
 *   - disputed → navigate to `AdjudicationFlowPage` at
 *     `/v4/go-lab/annotate/review/:recordId` (existing resolution-pane
 *     flow; B11).
 *   - agreed   → open `FinaliseAgreedDialog` in place with the record id.
 *     The queue stays visible behind. Confirming POSTs to
 *     `/v1/golab/annotation/records/{id}/finalise` (B19-BE) — no
 *     resolution row is written, sign-off is implicit.
 *
 * The `QueueStatStrip` (two cards: records awaiting review + completed
 * today) mirrors what the annotation queue shows so both tabs have the
 * same top-of-page summary chrome. Production polish (primary/second
 * annotator columns, time-in-queue, disputed-dimensions chips, explicit
 * per-row action buttons) is flagged in §3.5.1 and out of scope for B19.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../ui/button';
import { useCompletedToday, useReviewQueue } from './hooks/useAnnotation';
import QueueStatStrip from './components/queue/QueueStatStrip';
import QueueTable from './components/queue/QueueTable';
import FinaliseAgreedDialog from './components/adjudication/FinaliseAgreedDialog';
import type { DatasetRecord } from './types';

const PAGE_SIZE = 50;

const SkeletonTable: React.FC = () => (
  <div className="space-y-2" data-testid="review-queue-loading">
    {[1, 2, 3, 4, 5].map((i) => (
      <div
        key={i}
        className="h-12 animate-pulse rounded-md border border-border/70 bg-card"
      />
    ))}
  </div>
);

const ErrorPanel: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div
    className="flex h-[40vh] flex-col items-center justify-center text-center"
    data-testid="review-queue-error"
  >
    <p className="mb-4 text-muted-foreground">Failed to load the review queue.</p>
    <Button variant="link" size="sm" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

const ReviewQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useReviewQueue({
    limit: PAGE_SIZE,
    offset: 0,
  });
  const { data: completedTodayData } = useCompletedToday();

  /** Non-null while the FinaliseAgreedDialog is open for this record id. */
  const [finaliseRecordId, setFinaliseRecordId] = useState<string | null>(null);

  const handleRowClick = (record: DatasetRecord) => {
    if (record.status === 'disputed') {
      navigate(`/v4/go-lab/annotate/review/${record.id}`);
      return;
    }
    if (record.status === 'agreed') {
      setFinaliseRecordId(record.id);
      return;
    }
    // BE contract (B19-BE `list_review_queue`) returns only AGREED +
    // DISPUTED. Future status additions would silently no-op here rather
    // than crashing; not worth a defensive throw.
  };

  return (
    <div className="space-y-6">
      <QueueStatStrip
        recordsInQueue={data?.total ?? null}
        completedToday={completedTodayData?.finalisations ?? null}
        recordsLabel="Records awaiting review"
      />
      {isLoading ? (
        <SkeletonTable />
      ) : isError ? (
        <ErrorPanel onRetry={() => refetch()} />
      ) : (
        <QueueTable
          records={data?.items ?? []}
          onRowClick={handleRowClick}
          emptyStateCopy="No records awaiting review."
        />
      )}

      {/* Mount only while a record id is selected so the inner
          `useFinaliseRecord(recordId ?? '')` mutation hook is always
          bound to the right id — re-opening with a new id starts with
          a fresh mutation instance instead of leaking the prior one
          for the first render. */}
      {finaliseRecordId !== null && (
        <FinaliseAgreedDialog
          open
          recordId={finaliseRecordId}
          onOpenChange={(open) => {
            if (!open) setFinaliseRecordId(null);
          }}
        />
      )}
    </div>
  );
};

export default ReviewQueuePage;
